import { Dictionary, EntityAdapter, EntityState } from "@reduxjs/toolkit";

import { createDraftSafeSelector } from "./createSelector.js";

export interface NullableEntitySelectors<TState, T, IdType extends string> {
  selectAll: (state: TState) => T[] | undefined | null;
  selectIds: (state: TState) => IdType[] | undefined | null;
  selectEntities: (state: TState) => Dictionary<T> | undefined | null;
  selectTotal: (state: TState) => number | undefined | null;
  selectById: (state: TState, id: IdType) => T | undefined | null;
}

/**
 * Like `EntityAdapter.getSelectors`, except returns gracefully handles the underlying state not existing
 */
export const getSelectorsForNullableEntityState = <
  TState,
  T,
  IdType extends string = T extends { id: string } ? T["id"] : string,
>(
  adapter: EntityAdapter<T>,
  stateGetter: (state: TState) => EntityState<T> | null | undefined,
): NullableEntitySelectors<TState, T, IdType> => {
  const baseSelectors = adapter.getSelectors();

  // wraps a selector so that if the underlying state chunk doesn't exist, it just returns null.
  const selectorWrapper =
    <Args extends unknown[], R>(
      func: (state: EntityState<T>, ...args: Args) => R,
    ) =>
    (state: TState, ...args: Args) => {
      const entityState = stateGetter(state);
      return entityState == null ? null : func(entityState, ...args);
    };

  const baseSelectById = selectorWrapper(baseSelectors.selectById);
  const baseSelectIds = selectorWrapper(baseSelectors.selectIds);

  return {
    selectAll: selectorWrapper(baseSelectors.selectAll),
    // manually type the arguments here so that we only accept the specific Id type of the given Entity.
    selectById: (state: TState, id: IdType) => baseSelectById(state, id),
    selectIds: (state: TState) => baseSelectIds(state) as IdType[],
    selectTotal: selectorWrapper(baseSelectors.selectTotal),
    selectEntities: selectorWrapper(baseSelectors.selectEntities),
  };
};

export interface NonNullableEntitySelectors<TState, T, IdType extends string> {
  selectAll: (state: TState) => T[];
  selectIds: (state: TState) => IdType[];
  selectEntities: (state: TState) => Dictionary<T>;
  selectTotal: (state: TState) => number;
  selectById: (state: TState, id: IdType) => T | undefined | null;
}

/**
 * Like `EntityAdapter.getSelectors`, but with
 *   (1) properly typed ID parameter and
 *   (2) the consumer has already validated that the state is non-null
 *      => avoiding the `| null` return types of `getSelectorsForNullableEntityState`.
 */
export const getSelectorsForNonNullableEntityState = <
  TState,
  T,
  IdType extends string = T extends { id: string } ? T["id"] : string,
>(
  adapter: EntityAdapter<T>,
  stateGetter: (state: TState) => EntityState<T>,
): NonNullableEntitySelectors<TState, T, IdType> => {
  const baseSelectors = adapter.getSelectors(stateGetter);

  return {
    selectAll: baseSelectors.selectAll,
    selectById: (state: TState, id: IdType) =>
      baseSelectors.selectById(state, id),
    selectIds: (state: TState) => baseSelectors.selectIds(state) as IdType[],
    selectTotal: baseSelectors.selectTotal,
    selectEntities: baseSelectors.selectEntities,
  };
};

export interface EntitySelectorsWithSoftDelete<
  TState,
  T,
  IdType extends string,
> extends Omit<
  NullableEntitySelectors<TState, T, IdType>,
  // we can implement automatic-filtering versions of these selectors as needed
  "selectIds" | "selectTotal"
> {
  selectAllWithDeleted: (state: TState) => T[] | undefined | null;
  selectIdsWithDeleted: (state: TState) => IdType[] | undefined | null;
  selectEntitiesWithDeleted: (
    state: TState,
  ) => Dictionary<T> | undefined | null;
  selectTotalWithDeleted: (state: TState) => number | undefined | null;
  selectByIdWithDeleted: (state: TState, id: IdType) => T | undefined | null;
}

/**
 * Like `getSelectorsForNullableEntityState`, but the main selectors filter out soft-deleted elements.
 */
export const getSelectorsForEntityStateWithSoftDelete = <
  TState,
  T extends { deletedDate: string | null | undefined },
  IdType extends string = T extends { id: string } ? T["id"] : string,
>(
  adapter: EntityAdapter<T>,
  stateGetter: (state: TState) => EntityState<T> | null | undefined,
): EntitySelectorsWithSoftDelete<TState, T, IdType> => {
  const baseSelectors = getSelectorsForNullableEntityState<TState, T, IdType>(
    adapter,
    stateGetter,
  );

  return {
    selectAll: createDraftSafeSelector([baseSelectors.selectAll], (entities) =>
      entities == null ? null : entities.filter((e) => e.deletedDate == null),
    ),
    selectById: (state: TState, id: IdType) => {
      const entity = baseSelectors.selectById(state, id);
      return entity?.deletedDate != null ? null : entity;
    },
    selectEntities: createDraftSafeSelector(
      [baseSelectors.selectEntities],
      (entities) =>
        entities == null
          ? null
          : Object.fromEntries(
              Object.entries(entities).filter(
                ([_, ele]) => ele && ele.deletedDate == null,
              ),
            ),
    ),
    selectAllWithDeleted: baseSelectors.selectAll,
    selectByIdWithDeleted: baseSelectors.selectById,
    selectEntitiesWithDeleted: baseSelectors.selectEntities,
    selectIdsWithDeleted: baseSelectors.selectIds,
    selectTotalWithDeleted: baseSelectors.selectTotal,
  };
};
